import React from "react";
import { useEffect, useState } from "react";

import { Table, InputPicker, TagGroup, Tag } from "rsuite";
import Button from "rsuite/Button";
import EditIcon from "@rsuite/icons/Edit";
import FunnelIcon from "@rsuite/icons/Funnel";

import ConfigurationPage from "templates/ConfigurationPage";
import TableWithSearch from "components/TableWithSearch";
import FilterSidePanel from "./FilterSidePanel";
import ResidentsTableEmptyMessage from "./ResidentsTableEmptyMessage";
import useRequestHandler from "hooks/useRequestHandler";
import { uniqueValuesArray } from "./ResidentContent/partial/util";

const moment = require("moment-timezone");
const { Column, HeaderCell, Cell } = Table;

const List = ({ history, auth }) => {

    const [residentsListLoading, setResidentsListLoading] = useState(false);
    const [filterPanelVisibility, setFilterPanelVisibility] = useState(false);
    const [patientsList, setPatientsList] = useState([]);
    const [filteredPatientsData, setFilteredPatientsData] = useState([]);
    const [searchedData, setSearchedData] = useState([]);

    const [locationsList, setLocationsList] = useState([]);
    const [sectionsList, setSectionsList] = useState([]);
    const [roomsList, setRoomsList] = useState([]);

    const [filterData, setFilterData] = useState({
        facility: null,
        section: null,
        room_name: null
    });

    const [applyFilter, setApplyFilter] = useState(false);

    useEffect(() => {
        getPatients();
    }, [])

    const getPatientsRequest = useRequestHandler({
        auth,
        apiArguments: [{ orgId: auth.currentUser.defaultOrg }],
        apiName: "getAllPatients",
        successMessageData: { show: false },
        errorMessageData: {
            title: "Warning",
            message: "Unable to load patients, just add a new one.",
            type: "info"
        }
    })

    const getPatients = async () => {
        setResidentsListLoading(true);
        await getPatientsRequest(data => {
            const newData = data.map((patient) => ({ ...patient, dob: moment(patient.dob).format("l") }))
            setPatientsList(newData);
            setFilteredPatientsData(newData);
        });

        setResidentsListLoading(false);
    }

    const addNewResident = () => history.push(`/configuration/residents/new-resident/`);

    const goToEditPatientPage = ({ id }, { target }) => {
        if (target.ariaLabel === "edit" || (target.nearestViewportElement?.ariaLabel?.includes("edit"))) {
            history.push(`/configuration/residents/${id}/edit`);
        }
    }

    const closeFilterPanel = () => setFilterPanelVisibility(false);

    const addResident = () => {
        return <Button onClick={addNewResident} color="cyan" appearance="primary" size="lg">
            New Resident
        </Button>
    }

    const onOpenHandler = () => {
        setLocationsList(uniqueValuesArray(availableList(), "facility"));
        setAvailableSection();
        setAvailableRoom();
    }

    const availableList = (list) => searchedData?.length ? searchedData: list ||patientsList;

    const setAvailableSection = () => {
        if(filterData.facility) {
            const patientsByLocation = (availableList()).filter(({ facility }) => facility === filterData.facility);
            setSectionsList(uniqueValuesArray(patientsByLocation, "section"));
        } else {
            setSectionsList(uniqueValuesArray(availableList(), "section"));  
        }
    }

    const setAvailableRoom = () => {
        if(filterData.section) {
            setRoomsList(uniqueValuesArray(availableList(filteredPatientsData), "room_name"));
        } else {
            setRoomsList(uniqueValuesArray(availableList(), "room_name"));
        }
    }

    const addFilterLayout = () => (
        <Button disabled={filterPanelVisibility} appearance="ghost" color="cyan" className={"filter-icon-btn"}>
            <FunnelIcon color="cyan" style={{ fontSize: "2em" }} onClick={() => setFilterPanelVisibility(true)} />
        </Button>);

    const changeLocationHandler = (value) => {
        if (value) {
            setFilterData({ ...filterData, facility: value });
            const patientsByLocation = (availableList()).filter(({ facility }) => facility === value);
            setSectionsList(uniqueValuesArray(patientsByLocation, "section"));
            filterData.section || setRoomsList(uniqueValuesArray(patientsByLocation, "room_name"));
        } else {
           setFilterData({ ...filterData, facility: null });
           setSectionsList(uniqueValuesArray(availableList(), "section"));
           setAvailableRoom();
        }
    }

    const changeSectionHandler = (value) => {
        if (value) {
            setFilterData({ ...filterData, section: value });
            const patientsBySection = (availableList()).filter(({ section }) => section === value);
            setLocationsList(uniqueValuesArray(patientsBySection, "facility"));
            setRoomsList(uniqueValuesArray(patientsBySection, "room_name"));
        } else {
            setFilterData({ ...filterData, section: null });
            setAvailableRoom();
        }
    }

    const changeRoomHandler = (value) => {
        if (value) {
            setFilterData({ ...filterData, room_name: value });
            const patientsByRoom = (availableList()).filter(({ room_name }) => room_name === value);
            setLocationsList(uniqueValuesArray(patientsByRoom, "facility"));
            setSectionsList(uniqueValuesArray(patientsByRoom, "section"));
        } else {
            setFilterData({ ...filterData, room_name: null });
            setAvailableSection();
        }
    }

    const handlerFilterChange = (name, value) => {
        if (name === "location") changeLocationHandler(value);
        if (name === "section") changeSectionHandler(value);
        if (name === "room") changeRoomHandler(value);
    }

    const applyFilterHandler = () => {
        closeFilterPanel();
        filterHandler(filterData);
    }

    const clearFiltersHandler = () => {
        filterPanelVisibility && closeFilterPanel();
        setFilteredPatientsData(patientsList);
        setApplyFilter(false);
        setFilterData({});
    }

    const filterHandler = (data) => {
        const dataFilterExist = Object.values(data).filter(item => item);

        if (!!dataFilterExist.length) {
            setApplyFilter(true);
            let filteredPatients = [];

            for (const key in data) {
                filteredPatients = data[key] ? patientsList.filter(item => item[key] === data[key]) : filteredPatients;
            }

            setFilteredPatientsData(filteredPatients);
        } else {
            setFilteredPatientsData(patientsList);
            setApplyFilter(false);
        }
    }

    const removeTag = tag => {
        let newFiltersData = {};

        for (const key in filterData) {
            if (filterData[key] == tag) newFiltersData = { ...filterData, [key]: null };
        }

        setFilterData(newFiltersData);
        filterHandler(newFiltersData);
    };

    const filtersData = () => (<div className={`list-filters-data`}>
        <div>Filters: </div>
        <TagGroup>
            {Object.values(filterData).filter(item => item).map((item, index) => (
                <Tag key={index} closable onClose={() => removeTag(item)}>
                    {item}
                </Tag>
            ))}
            <Tag closable onClose={clearFiltersHandler} className={"clear-filter"}>
                Clear All Filters
            </Tag>
        </TagGroup>
    </div>)

    return (
        <ConfigurationPage
            pageTitle="Residents"
            subnavRight={addResident()}
            history={history}
        >
            <ConfigurationPage.Content>
                <FilterSidePanel
                    panelOpened={filterPanelVisibility}
                    onCloseHandler={closeFilterPanel}
                    clearFiltersHandler={clearFiltersHandler}
                    onOpenHandler={onOpenHandler}
                    auth={auth}
                    applyFilterHandler={applyFilterHandler}
                >
                    <div className="filter-wrapper">
                        <div className="filter">
                            <label>Location</label>
                            <InputPicker
                                data={locationsList}
                                className="filter-group"
                                placeholder={"Select location"}
                                onChange={(value) => handlerFilterChange("location", value)}
                                value={filterData.facility}
                            />
                        </div>

                        <div className="filter">
                            <label>Section</label>
                            <InputPicker
                                data={sectionsList}
                                className="filter-group"
                                placeholder={"Select section"}
                                onChange={(value) => handlerFilterChange("section", value)}
                                value={filterData.section}
                            />
                        </div>

                        <div className="filter">
                            <label>Room</label>
                            <InputPicker
                                data={roomsList}
                                className="filter-group"
                                placeholder={"Select room"}
                                onChange={(value) => handlerFilterChange("room", value)}
                                value={filterData.room_name}
                            />
                        </div>
                    </div>
                </FilterSidePanel>

                <TableWithSearch
                    data={filteredPatientsData}
                    dataLoading={residentsListLoading}
                    setData={setFilteredPatientsData}
                    searchData ={searchedData}
                    setSearchData={setSearchedData}
                    searchInputPlaceholder="Search patients by Last Name"
                    rightContent={addFilterLayout}
                    searchBy="lastName"
                    applyFilter={applyFilter}
                    filtersData={filtersData}
                    locale={ResidentsTableEmptyMessage}
                    onRowClick={goToEditPatientPage}
                >
                    <Column flexGrow={4} sortable>
                        <HeaderCell><span className="header-cell">Last Name</span></HeaderCell>
                        <Cell dataKey="lastName" />
                    </Column>

                    <Column flexGrow={4} sortable>
                        <HeaderCell><span className="header-cell">First Name</span></HeaderCell>
                        <Cell dataKey="firstName" />
                    </Column>

                    <Column flexGrow={4} sortable>
                        <HeaderCell><span className="header-cell">Dob</span></HeaderCell>
                        <Cell dataKey="dob" />
                    </Column>
                    <Column flexGrow={4} sortable>
                        <HeaderCell><span className="header-cell">Gender</span></HeaderCell>
                        <Cell dataKey="gender" />
                    </Column>
                    <Column flexGrow={4}>
                        <HeaderCell><span className="header-cell">Location</span></HeaderCell>
                        <Cell dataKey="facility" />
                    </Column>
                    <Column flexGrow={4}>
                        <HeaderCell><span className="header-cell">Section</span></HeaderCell>
                        <Cell dataKey="section" />
                    </Column>
                    <Column flexGrow={4}>
                        <HeaderCell><span className="header-cell">Room</span></HeaderCell>
                        <Cell dataKey="room_name" />
                    </Column>
                    <Column flexGrow={3}>
                        <HeaderCell><span className="header-cell">Bed</span></HeaderCell>
                        <Cell dataKey="bed_name" />
                    </Column>

                    <Column flexGrow={2} align="right">
                        <HeaderCell><span className="header-cell"></span></HeaderCell>
                        <Cell>
                            <EditIcon color="cyan" className="edit" />
                        </Cell>
                    </Column>

                </TableWithSearch>
            </ConfigurationPage.Content>
        </ConfigurationPage>
    );
};

export default List;